import type { SectionContent } from '@/types'

function isTruthyDirty(value: unknown): boolean {
  return value === true || value === 1
}

function hasVisibleContent(value: string | undefined): boolean {
  return (value ?? '').trim().length > 0
}

function contentLength(value: string | undefined): number {
  return (value ?? '').length
}

function toTimestamp(value: string | Date | undefined): number {
  if (!value) return 0
  if (value instanceof Date) return value.getTime()
  const ts = Date.parse(value)
  return Number.isFinite(ts) ? ts : 0
}

export function getSectionContentCompositeKey(content: Pick<SectionContent, 'sectionId' | 'versionId'>): string {
  return `${content.sectionId}:${content.versionId}`
}

export function compareCanonicalSectionContents(a: SectionContent, b: SectionContent): number {
  const aDeleted = !!a.deletedAt
  const bDeleted = !!b.deletedAt
  if (aDeleted !== bDeleted) return aDeleted ? 1 : -1

  const aVisible = hasVisibleContent(a.content)
  const bVisible = hasVisibleContent(b.content)
  if (aVisible !== bVisible) return aVisible ? -1 : 1

  const aDirty = isTruthyDirty(a.dirty)
  const bDirty = isTruthyDirty(b.dirty)
  if (aDirty !== bDirty) return aDirty ? -1 : 1

  const aUpdatedAt = toTimestamp(a.updatedAt ?? a.createdAt)
  const bUpdatedAt = toTimestamp(b.updatedAt ?? b.createdAt)
  if (aUpdatedAt !== bUpdatedAt) return bUpdatedAt - aUpdatedAt

  const aLength = contentLength(a.content)
  const bLength = contentLength(b.content)
  if (aLength !== bLength) return bLength - aLength

  return a.id.localeCompare(b.id)
}

export function pickCanonicalSectionContent(contents: SectionContent[]): SectionContent | undefined {
  if (contents.length === 0) return undefined
  return [...contents].sort(compareCanonicalSectionContents)[0]
}

export function groupSectionContentsByCompositeKey(contents: SectionContent[]): Map<string, SectionContent[]> {
  const grouped = new Map<string, SectionContent[]>()
  for (const content of contents) {
    const key = getSectionContentCompositeKey(content)
    const rows = grouped.get(key) ?? []
    rows.push(content)
    grouped.set(key, rows)
  }
  return grouped
}

export function canonicalizeSectionContents(contents: SectionContent[]): SectionContent[] {
  const grouped = groupSectionContentsByCompositeKey(contents)
  return [...grouped.values()]
    .map((group) => pickCanonicalSectionContent(group))
    .filter((content): content is SectionContent => Boolean(content))
}
